// Popup "Atualize o aplicativo" (pedido 2026-10-21): só dentro do app
// nativo (Capacitor). Compara o build instalado (App.getInfo().build =
// versionCode) com o publicado na loja que o Super Master informou em
// Aplicativo → "Aviso de atualização na loja". Build menor → popup com
// "Atualizar", que abre a página do app na Play Store. Atualização só do
// sistema (site) continua no UpdateAvailableNotice (recarrega sozinho).
// "Agora não" esconde até o app voltar do segundo plano / reabrir.
import { useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Download, Sparkles } from "lucide-react";
import { useAdminBranding } from "@/hooks/queries";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { gpsDriveAppStoreVersionsService } from "@/services/gpsdrive-app-releases.service";

interface Pending {
  storeUrl: string;
  message: string;
  installed: string;
}

export function AppStoreUpdatePrompt() {
  const [pending, setPending] = useState<Pending | null>(null);
  const { data: branding } = useAdminBranding();

  const check = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const info = await App.getInfo();
      const build = Number(info.build);
      if (!Number.isFinite(build)) return;
      const remote = await gpsDriveAppStoreVersionsService.publicVersion(info.id, info.build);
      if (remote.enabled && remote.latestBuild && remote.storeUrl && build < remote.latestBuild) {
        setPending({ storeUrl: remote.storeUrl, message: remote.message ?? "", installed: info.version });
      } else {
        setPending(null);
      }
    } catch {
      // sem rede / plugin ausente — tenta de novo na próxima abertura
    }
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    void check();
    const sub = App.addListener("resume", () => void check());
    return () => {
      void sub.then((h) => h.remove());
    };
  }, [check]);

  if (!pending) return null;
  const logo = branding?.faviconUrl ?? branding?.logoUrl;

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/60 p-4 backdrop-blur-sm sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-update-title"
        className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl animate-in fade-in slide-in-from-bottom-6 duration-300 dark:bg-slate-900"
        style={{ marginBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="relative flex flex-col items-center bg-primary px-6 pb-8 pt-7 text-primary-foreground">
          <span className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/15 blur-xl" aria-hidden />
          <div className="relative grid h-20 w-20 place-items-center overflow-hidden rounded-[22px] bg-white shadow-lg ring-4 ring-white/30">
            {logo ? (
              <img src={resolveGpsDriveUrl(logo)} alt="" className="h-full w-full object-contain p-2" />
            ) : (
              <Download className="h-9 w-9 text-primary" />
            )}
          </div>
          <span className="relative mt-4 inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider">
            <Sparkles className="h-3 w-3" /> Nova versão
          </span>
        </div>
        <div className="px-6 pb-6 pt-5 text-center">
          <h2 id="app-update-title" className="text-xl font-bold text-slate-900 dark:text-white">
            Atualize o aplicativo
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            {pending.message ||
              "Uma nova versão do aplicativo está disponível na Play Store, com melhorias e correções. Leva menos de um minuto."}
          </p>
          <p className="mt-1 text-xs text-slate-400">Sua versão: {pending.installed}</p>
          <button
            type="button"
            onClick={() => {
              window.location.href = pending.storeUrl;
            }}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 text-base font-semibold text-primary-foreground shadow-lg transition active:scale-[0.98]"
          >
            <Download className="h-5 w-5" /> Atualizar
          </button>
          <button
            type="button"
            onClick={() => setPending(null)}
            className="mt-2 w-full rounded-2xl py-2.5 text-sm font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Agora não
          </button>
        </div>
      </div>
    </div>
  );
}
